import { formatISO } from "date-fns";
import React, { useEffect, useRef } from "react";
import { z } from "zod";

import {
	ButtonStandard,
	type FormHandle,
	FormTextEditor,
	ModalBase,
	PYForm,
	SubmitButton,
	Text,
} from "@performyard/py-components";

import { useMutatePostGoalHistory } from "../../../../api/hooks";

type GoalAddMessageModalProps = {
	goalId?: string;
	goalName?: string;
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
};

const formSchema = z.object({
	message: z.string().min(1, "Please enter a message"),
});

type FormValues = z.infer<typeof formSchema>;

const GoalAddMessageModal = ({
	goalId,
	goalName,
	isOpen,
	onClose,
	onSuccess,
}: GoalAddMessageModalProps): React.ReactElement => {
	const formRef = useRef<FormHandle<typeof formSchema>>(null);

	const { mutate, isPending } = useMutatePostGoalHistory({
		onSuccess: () => {
			onSuccess?.();
			onClose();
		},
	});

	useEffect(() => {
		if (isOpen) {
			formRef.current?.reset();
		}
	}, [isOpen]);

	const handleSubmit = async ({ value }: { value: FormValues }): Promise<void> => {
		if (!goalId) {
			return;
		}

		mutate({ goalID: goalId, data: { message: value.message, as_of: formatISO(new Date()) } });
	};

	return (
		<ModalBase isOpen={isOpen} onClose={onClose} dangerouslySetClassName="w-[432px]">
			<div className="px-6 pb-4 text-center">
				<Text size="sub-heading" weight="semi-bold">
					Add Message
				</Text>
				{goalName ? (
					<Text size="paragraph-2" weight="regular">
						{goalName}
					</Text>
				) : null}
			</div>

			<PYForm
				ref={formRef}
				id="goal-add-message"
				schema={formSchema}
				defaultValues={{ message: "" }}
				onSubmit={handleSubmit}
			>
				<div className="px-6 pb-6">
					<FormTextEditor
						id="message"
						label="Message"
						placeholder="Add Message"
						minHeight={68}
						maxHeight={100}
						data-testid="meeting-goal-add-message"
					/>
				</div>

				<div className="flex justify-center gap-2 px-6 pb-6">
					<div className="w-fit min-w-[90px]">
						<ButtonStandard
							variant="outline"
							color="neutral"
							size="large"
							onClick={onClose}
							disabled={isPending}
							data-testid="cancel-add-message"
						>
							Cancel
						</ButtonStandard>
					</div>
					<div className="w-[140px]">
						<SubmitButton
							color="primary"
							size="large"
							disabled={!goalId || isPending}
							loading={isPending}
							data-testid="submit-add-message"
						>
							Add Message
						</SubmitButton>
					</div>
				</div>
			</PYForm>
		</ModalBase>
	);
};

export default GoalAddMessageModal;
